<template>
  <BaseModal
    class="card-modal app-container"
    :full-screen="isMobile"
    flush-content
    ui-refresh
    @close-modal-clicked="emit('closed')">
    <template #body>
      <div class="layout-container">
        <CardModalBody
          :title="title"
          :text="text"
          :image-url="imageUrl">
          <template #text>
            <slot
              v-if="$slots.text"
              name="text"/>
          </template>
        </CardModalBody>
      </div>
    </template>
    <template #footer>
      <CardModalButtons>
        <template #primary-button="props">
          <slot
            name="primary-button"
            v-bind="props"/>
        </template>
        <template
          v-if="$slots['secondary-button']"
          #secondary-button="props">
          <slot
            name="secondary-button"
            v-bind="props"/>
        </template>
        <template
          v-if="$slots['bottom-button']"
          #bottom-button="props">
          <slot
            name="bottom-button"
            v-bind="props"/>
        </template>
      </CardModalButtons>
    </template>
  </BaseModal>
</template>

<script setup>
import BaseModal from '@/components/global/BaseModal.vue'
import useScreenSize from '@shared/composables/screenSize.js'
import { onBeforeUnmount, onMounted } from 'vue'
import { useHideIntercomLauncher } from '@shared/composables/intercom.js'
import CardModalButtons from '@/components/modals/CardModalButtons.vue'
import CardModalBody from '@/components/modals/CardModalBody.vue'
import useAnalytics from '@shared/composables/analytics.js'

const props = defineProps({
  name: {
    type: String,
    required: true
  },
  title: {
    type: String,
    required: true
  },
  text: {
    type: [String, Array],
    default: null
  },
  imageUrl: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['closed'])

useHideIntercomLauncher()
const { isMobile } = useScreenSize()

const { trackOverlayInteraction } = useAnalytics()
onMounted(() => { trackOverlayInteraction(props.name, 'modal', 'open') })
onBeforeUnmount(() => { trackOverlayInteraction(props.name, 'modal', 'dismiss') })
</script>

<style lang="scss" scoped>
.card-modal {
  // Modal Backdrop Styles
  :deep(.modal-backdrop) {
    --bs-backdrop-opacity: 0.75;
    --bs-backdrop-bg: #{$armor};
  }

  // Base Modal Content Styles
  :deep(.modal-content) {
    background-color: $origami;
    display: grid;
    grid-template-rows: auto 1fr auto;
    
    &:not(.full-screen) {
      border-radius: 2px;
    }
  }

  // Make every parent element of the image full height
  :deep(.scrollable) {
    &, .container-fluid {
      height: 100%;
    }
  }
  :deep(.carousel-slide) {
    &, & > * {
      height: 100% !important;
    }
  }

  // Modal Header Styles
  :deep(.modal-header) {
    @include transition-base(all, $transition-duration: 1.2s);
    padding: $spacing-md;

    .close-button {
      color: $armor;
      width: 32px;
      height: 32px;
      padding: 4px;
      top: 11px;
      right: 11px;
      position: unset;

      svg {
        width: 24px;
        height: 24px;
      }
    }
  }

  // Modal Footer Styles
  :deep(.footer) {
    @include transition-base(all, $transition-duration: 1.2s);
    background: $origami;
    padding: $spacing-md !important;
    border: none;
    position: unset;
  }

  // Layout Container Styles
  .layout-container {
    display: grid;
    height: 100%;
  }
}
</style>
